import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { Loader } from '@/components/custom/loader';
import { ExamCover } from './ExamCover';
import HeaderPrinted from './HeaderPrinted';
import IdentificationForm from './IdentificationForm';
import AnswerKey from './AnswerKey';
import { ARUCO_MARKERS } from './arucoMarkers';
import artwork from './assets/applicator/cover-artwork.svg';
import megaphone from './assets/megaphone.svg';
import './applicator-booklet.css';

// Inspect visible HTML text, never tag names, image alt text or formula markup.
export function hasReadableText(html) {
  if (!html) return false;
  const element = document.createElement('div');
  element.innerHTML = String(html);
  element.querySelectorAll('img, svg, math, script, style, annotation, [hidden], [aria-hidden="true"], .katex, .MathJax, .math-tex, .ck-math-widget, [data-math], [data-equation]').forEach(node => node.remove());
  element.querySelectorAll('br').forEach(node => node.replaceWith(' '));
  element.querySelectorAll('p, div, li, td').forEach(node => node.appendChild(document.createTextNode(' ')));
  const text = (element.textContent || '')
    .replace(/\$\$[\s\S]*?\$\$|\$[^$\n]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]/g, ' ')
    .replace(/\\(?:frac|sqrt|sum|int|begin|end|mathrm|mathbf|text|left|right|times|div|cdot|alpha|beta|theta|pi)(?:\[[^\]]*\])?(?:\{[^{}]*\})*/g, ' ')
    .replace(/\bR\s*\$/g, ' ')
    .replace(/\b\d+(?:[.,]\d+)?\s*(?:km|cm|mm|dm|kg|mg|ml|mL|ms|hz|Hz|kWh|km\/h|m\/s|°\s*[CF]|[mglLsShWVA])(?:[²³]|\^\d)?\b/g, ' ')
    .trim();
  if (!text) return false;
  // Plain algebra can contain letters (x + y), functions (sen, cos) and units.
  const words = text.match(/\p{L}+/gu) || [];
  if (/[\d=+×÷*/^√<>≤≥−-]/.test(text) && words.every(word => word.length === 1 || /^(?:sen|sin|cos|tan|tg|log|ln|sqrt|pi|cm|mm|km|kg|ml|hz)$/i.test(word))) return false;
  return /\p{L}{2,}/u.test(text);
}

function ReadingIcon() {
  return (
    <img
      className="applicator-reading-icon"
      src={megaphone}
      alt="Ler em voz alta"
      width="21"
      height="20"
    />
  );
}

function prepareReadingContent(html) {
  const readable = hasReadableText(html);
  if (!readable) return { html, leadingIcon: false };
  const root = document.createElement('div');
  root.innerHTML = html;
  const media = [...root.querySelectorAll('img, svg, math')];
  if (!media.length) return { html, leadingIcon: true };

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (node.parentElement.closest('svg, math, script, style, [hidden], [aria-hidden="true"], .katex, .MathJax, .math-tex, .ck-math-widget, [data-math], [data-equation]')) continue;
    if (!hasReadableText(node.textContent)) continue;
    // Keep an image-only prefix outside the reading cue, including when the
    // image and its caption share the same paragraph or formatting element.
    if (media.some(image => image.compareDocumentPosition(node) & Node.DOCUMENT_POSITION_FOLLOWING)) {
      const icon = document.createElement('img');
      icon.src = megaphone;
      icon.alt = 'Ler em voz alta';
      icon.className = 'applicator-reading-icon';
      icon.width = 21;
      icon.height = 20;
      node.parentNode.insertBefore(icon, node);
      return { html: root.innerHTML, leadingIcon: false };
    }
    break;
  }
  return { html, leadingIcon: true };
}

function QuestionContent({ html, boxed = false }) {
  const content = useMemo(() => prepareReadingContent(html), [html]);
  if (!html) return null;
  return (
    <div className={boxed ? 'applicator-question-content applicator-question-content--boxed' : 'applicator-question-content'}>
      {content.leadingIcon && <ReadingIcon />}
      <div
        className="applicator-question-content__html"
        dangerouslySetInnerHTML={{ __html: content.html }}
      />
    </div>
  );
}

export function ApplicatorPrintedQuestion({ question, index }) {
  return (
    <div className="printed-question applicator-question">
      <div className="printed-question__header">
        <h3 className="printed-question__title">Questão {String(index + 1).padStart(2, '0')}</h3>
        <p className="printed-question__tag">Q{question?.id}</p>
      </div>
      {(question?.['motivator-texts'] || []).map((reference, referenceIndex) => (
        <React.Fragment key={reference.id ?? referenceIndex}>
          <QuestionContent
            html={reference.body}
            boxed
          />
          <QuestionContent html={reference.print} />
        </React.Fragment>
      ))}
      <QuestionContent html={question?.description} />
      <ul className="printed-question__alternatives">
        {question?.alternatives
          ?.filter(alternative => alternative['is-correct'] !== null)
          .map(alternative => {
            const content = prepareReadingContent(alternative.description);
            return (
              <li
                key={alternative.id}
                className="printed-question__alternative"
              >
                {content.leadingIcon && <ReadingIcon />}
                <span className="printed-question__alternative-id">{alternative.name}</span>
                <div
                  className="applicator-question-content__html"
                  dangerouslySetInnerHTML={{ __html: content.html }}
                />
              </li>
            );
          })}
      </ul>
    </div>
  );
}

function ApplicatorReadingNotice() {
  return (
    <div className="applicator-reading-notice">
      Aplicador, ler SOMENTE a(s) informação(ões) que apresenta(m) o desenho de um megafone. <ReadingIcon />
    </div>
  );
}

function ApplicatorExamHeader({ questionBook }) {
  const duration = questionBook?.['resolution-time'];
  const [hours, minutes] = (duration || '').split(':').map(Number);
  const durationLabel = [hours ? `${hours} ${hours === 1 ? 'hora' : 'horas'}` : '', minutes ? `${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}` : ''].filter(Boolean).join(' e ');
  const start = questionBook?.['published-at'];
  const finish = questionBook?.['finished-at'];
  return (
    <header className="applicator-exam-header">
      <HeaderPrinted logoOnly />
      <h2>{questionBook?.title}</h2>
      <div className="applicator-exam-header__meta">
        {(start || finish) && (
          <span>
            {start ? `De ${moment(start).format('DD/MM/YYYY')}` : ''}
            {finish ? ` a ${moment(finish).format('DD/MM/YYYY')}` : ''}
          </span>
        )}
        {durationLabel && <span>{durationLabel} de duração</span>}
        <span>{questionBook?.['question-count']} questões</span>
      </div>
      <div className="applicator-exam-header__identification">
        <p>Nome</p>
        <p>Escola</p>
        <div>
          <p>Série</p>
          <p>Turma</p>
        </div>
      </div>
    </header>
  );
}

function Megaphone() {
  return (
    <img
      className="applicator-instructions__megaphone"
      src={megaphone}
      alt="Megafone: ler em voz alta"
      width="21"
      height="20"
    />
  );
}

function ApplicatorInstructions() {
  return (
    <section
      className="applicator-instructions"
      aria-label="Orientações do aplicador"
    >
      <h2>EXEMPLO</h2>
      <p className="applicator-instructions__introduction">
        Aplicador, ler SOMENTE a(s) informação(ões) que apresenta(m) o desenho de um megafone. <Megaphone />
      </p>
      <p className="applicator-instructions__lead">Vamos fazer juntos um exemplo de uma atividade. Veja abaixo:</p>
      <div className="applicator-example">
        <div className="applicator-example__prompt applicator-example__prompt--first">
          <Megaphone />
          <p>Observe abaixo a conta que a professora Ana escreveu</p>
          <strong>Deve ser lido</strong>
        </div>
        <div className="applicator-example__calculation">
          <p>15 + 4</p>
          <strong>Não deve ser lido</strong>
        </div>
        <div className="applicator-example__prompt applicator-example__prompt--second">
          <Megaphone />
          <p>Qual é o resultado dessa conta?</p>
          <strong>Deve ser lido</strong>
        </div>
        <div className="applicator-example__answers">
          <div className="applicator-example__options">
            {[
              ['A', '11'],
              ['B', '19'],
              ['C', '18'],
              ['D', '55']
            ].map(([letter, value]) => (
              <div key={letter}>
                <b>{letter}</b>
                <span>{value}</span>
              </div>
            ))}
          </div>
          <strong>Não deve ser lido</strong>
        </div>
      </div>
    </section>
  );
}

// Preserve the existing answer grid and ArUco geometry for the blank master copy.
function ApplicatorAnswerSheet({ questions, institution }) {
  return (
    <section
      className="applicator-booklet__sheet printed-exam"
      aria-label="Folha de respostas do aplicador"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, border: '1px solid #d9d9d9', borderRadius: 6, padding: 12, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <HeaderPrinted institution={institution} />
          <IdentificationForm minimal />
        </div>
      </div>
      <div
        className="printed-exam__answer-sheet"
        style={{ position: 'relative', padding: 52 }}
      >
        {[
          { id: 0, style: { top: 0, left: 0 } },
          { id: 1, style: { top: 0, right: 0 } },
          { id: 2, style: { bottom: 0, right: 0 } },
          { id: 3, style: { bottom: 0, left: 0 } }
        ].map(marker => (
          <img
            key={marker.id}
            src={ARUCO_MARKERS[marker.id]}
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', width: 44, height: 44, imageRendering: 'pixelated', zIndex: 2, ...marker.style }}
          />
        ))}
        <div className="answer-key-wrapper">
          {questions.slice(0, 100).map((question, index) => (
            <AnswerKey
              key={question.id}
              question={question}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

const SUBJECT_NOTICE_COLORS = [
  { background: '#fff5cc', border: '#b76e00', text: '#7a4100' },
  { background: '#d3fcd2', border: '#118d57', text: '#005249' },
  { background: '#d6efff', border: '#1971a8', text: '#124b70' },
  { background: '#eee5ff', border: '#7950b8', text: '#513080' },
  { background: '#ffe4ec', border: '#b7436c', text: '#822c4c' },
  { background: '#d5f5f1', border: '#168477', text: '#09564d' }
];

// Presentation only: preserve the exact order and numbering used by the answer sheet.
function ApplicatorSubjectQuestions({ questionBook, questions = [], asTableRows = false }) {
  const blocks = questionBook?.['question-book-taxonomies'] || [];
  const links = questionBook?.['question-book-taxonomy-questions'] || [];
  const names = new Map((questionBook?.taxonomies || []).map(taxonomy => [String(taxonomy.id), taxonomy.name]));
  const blocksById = new Map(blocks.map(block => [String(block.id), block]));
  const colorsByTaxonomy = new Map();
  blocks.forEach(block => {
    const id = String(block.taxonomy_id ?? block['taxonomy-id']);
    if (!colorsByTaxonomy.has(id)) colorsByTaxonomy.set(id, SUBJECT_NOTICE_COLORS[colorsByTaxonomy.size % SUBJECT_NOTICE_COLORS.length]);
  });
  let previousSubject;
  let noticeCount = 0;

  return questions.map((question, index) => {
    const link = String(links[index]?.['question-id']) === String(question.id) ? links[index] : links.find(item => String(item['question-id']) === String(question.id));
    const block = blocksById.get(String(link?.['question-book-taxonomy-id'])) || blocks.find(item => (item.question_ids || item['question-ids'] || []).some(id => String(id) === String(question.id)));
    const taxonomyId = block?.taxonomy_id ?? block?.['taxonomy-id'];
    const name = names.get(String(taxonomyId));
    const showNotice = Boolean(name) && String(taxonomyId) !== previousSubject;
    previousSubject = name ? String(taxonomyId) : undefined;
    const first = noticeCount === 0;
    if (showNotice) noticeCount += 1;

    const content = (
      <ApplicatorPrintedQuestion
        question={question}
        index={index}
      />
    );
    const color = colorsByTaxonomy.get(String(taxonomyId)) || SUBJECT_NOTICE_COLORS[0];
    const questionContent = showNotice ? (
      <div
        key={`${question.id}-${index}`}
        className="applicator-subject-start"
      >
        <p
          className="applicator-subject-notice"
          style={{ '--subject-background': color.background, '--subject-border': color.border, '--subject-text': color.text }}
        >
          {first ? 'A seguir, você responderá ' : 'Agora, você responderá '}
          <strong>questões de {name}</strong>
        </p>
        {content}
      </div>
    ) : (
      <React.Fragment key={`${question.id}-${index}`}>{content}</React.Fragment>
    );
    return asTableRows ? (
      <tr
        className="applicator-question-row"
        key={`${question.id}-${index}`}
      >
        <td>{questionContent}</td>
      </tr>
    ) : (
      questionContent
    );
  });
}

// A table header repeats at every printed page without measuring or paginating HTML.
function ApplicatorQuestionDocument({ questionBook, questions }) {
  return (
    <div className="printed-exam applicator-question-document">
      <table className="applicator-question-table">
        <thead>
          <tr>
            <td>
              <ApplicatorReadingNotice />
            </td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <ApplicatorExamHeader questionBook={questionBook} />
            </td>
          </tr>
          <ApplicatorSubjectQuestions
            questionBook={questionBook}
            questions={questions}
            asTableRows
          />
        </tbody>
      </table>
    </div>
  );
}

function decodeImage(src) {
  const image = new Image();
  image.src = src;
  return image.decode();
}

export function ApplicatorBooklet() {
  const { id } = useParams();
  const client = useClient();
  const [result, setResult] = useState({ loading: true });

  useEffect(() => {
    let cancelled = false;
    setResult({ loading: true });
    async function load() {
      try {
        const [book, questions] = await Promise.all([
          client.fetch(`question_books/${parseInt(id)}`),
          client.fetch(`questions?filter[question_book_id]=${parseInt(id)}`),
          ...[artwork, megaphone].map(decodeImage),
          document.fonts?.load('900 32px Nunito'),
          document.fonts?.load('700 22px Nunito'),
          document.fonts?.load('400 14px Nunito')
        ]);
        if (book.error || questions.error || !book.data) throw new Error('request failed');
        const courseId = book.data['course-ids']?.[0];
        const course = courseId ? await client.fetch(`courses/${courseId}`) : { data: null };
        if (course.error) throw new Error('course request failed');
        const byId = new Map((questions.data || []).map(question => [String(question.id), question]));
        const ordered = book.data['question-book-taxonomy-questions']?.map(item => byId.get(String(item['question-id']))) || questions.data || [];
        // Missing questions must not silently create an incomplete master booklet.
        if (ordered.some(question => !question)) throw new Error('question missing');
        if (!cancelled) setResult({ loading: false, book: book.data, questions: ordered, course: course.data });
      } catch {
        if (!cancelled) setResult({ loading: false, error: 'Não foi possível carregar o caderno do aplicador. Atualize a página para tentar novamente.' });
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [client, id]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = result.book?.title ? `Caderno do aplicador - ${result.book.title}` : 'Caderno do aplicador';
    return () => {
      document.title = previousTitle;
    };
  }, [result.book?.title]);

  if (result.loading) return <Loader />;
  if (result.error) return <p role="alert">{result.error}</p>;

  return (
    <main className="applicator-booklet">
      <style media="print">{'@page { size: A4; margin: 0 !important; }'}</style>
      <ExamCover
        applicator
        title={result.book.title}
        description={result.book.description}
      />
      <div
        className="applicator-booklet__blank"
        aria-hidden="true"
      />
      <ApplicatorAnswerSheet
        questions={result.questions}
        institution={result.course?.institution?.name}
      />
      <div
        className="applicator-booklet__blank"
        aria-hidden="true"
      />
      <ApplicatorInstructions />
      <section
        className="applicator-booklet__questions"
        aria-label="Questões do caderno do aplicador"
      >
        <ApplicatorQuestionDocument
          questionBook={result.book}
          questions={result.questions}
        />
      </section>
    </main>
  );
}
